import React, { useCallback, useId } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useDispatch } from 'react-redux';

import SeverityBreakdown from 'src/screen/v2/Protected/DashboardScreen/AlarmsToTriage/SeverityBreakdown';
import { BreakdownSkeleton, TotalSkeleton } from 'src/screen/v2/Protected/DashboardScreen/AlarmsToTriage/AlarmsToTriageSkeleton';
import { Button } from 'src/component/ds/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'src/component/ds/ui/card';
import { cn } from 'src/component/ds/utils';
import { pushHistory } from 'src/module/navigation/action';
import { useTranslation } from 'src/i18n';
import { type AlarmsToTriageSegment } from 'src/screen/v2/Protected/DashboardScreen/AlarmsToTriage/severity';

export {
  ALARM_SEVERITIES,
  type AlarmSeverity,
  type AlarmsToTriageSegment
} from 'src/screen/v2/Protected/DashboardScreen/AlarmsToTriage/severity';

/* Every drill-in on this card lands on maintenance, the acknowledged pill
 * included. The alarm list is where ack status actually lives, but it can't
 * filter to it yet, so pointing there would dump the user in the full list. */
const MAINTENANCE_PATH = '/maintenance';

export interface AlarmsToTriageProps {
  /** Every alarm awaiting triage, which may exceed the segments' sum. */
  total?: number;
  acknowledged?: number;
  /** Up to three, most severe first. */
  segments?: AlarmsToTriageSegment[];
  /** Swaps the counts and bars for skeletons while they're being fetched. */
  loading?: boolean;
  className?: string;
}

/**
 * The dashboard's Alarms to Triage card: the headline count of alarms awaiting
 * triage, how many are acknowledged, and a proportional breakdown by severity.
 * Every control drills through to maintenance.
 *
 * Renders one of three states -- loading (skeletons), empty (count only), or
 * populated.
 *
 * @param {AlarmsToTriageProps} props see AlarmsToTriageProps
 * @returns {JSX.Element} the card
 */
export default function AlarmsToTriage ({
  total = 0,
  acknowledged = 0,
  segments = [],
  loading = false,
  className
}: AlarmsToTriageProps) {
  const { t, tPlural } = useTranslation();
  const dispatch = useDispatch();

  /* Maintenance reads the same filters from redux, so the area/unit scoping
   * the user already has applied carries across the navigation. */
  const openMaintenance = useCallback(() => {
    dispatch(pushHistory(MAINTENANCE_PATH));
  }, [dispatch]);

  /* Three mutually exclusive states rather than overlapping booleans: the
   * acknowledged pill and the breakdown each render in 'populated' only. */
  let state: 'loading' | 'empty' | 'populated' = 'populated';
  if (loading) state = 'loading';
  else if (total === 0) state = 'empty';


  const titleId = useId();

  return (
    <Card className={cn('ds-shadow-ds-sm', className)} data-testid='alarms-to-triage'>
      <CardHeader className='ds-gap-1 ds-p-8 ds-pb-4'>
        <CardTitle as='h2' id={titleId} className='ds-text-ds-sm ds-font-normal ds-text-muted-foreground'>
          {t('dashboard.alarmsToTriage')}
        </CardTitle>

        <div className='ds-flex ds-items-center ds-justify-between ds-gap-3'>
          <div className='ds-flex ds-items-center ds-gap-3'>
            {state === 'loading'
              ? <TotalSkeleton />
              : (
                <p
                  className='ds-font-heading ds-text-ds-h1 ds-font-medium ds-tracking-[-1.5px] ds-text-foreground'
                  data-testid='alarms-to-triage-total'
                  aria-describedby={titleId}
                >
                  {total}
                </p>
              )}

            {state === 'populated' && (
              <Button
                variant='secondary'
                size='xs'
                onClick={openMaintenance}
                data-testid='alarms-to-triage-acknowledged'
              >
                <span>{tPlural('dashboard.alarmsAcknowledged', acknowledged)}</span>
                <ChevronRightIcon fontSize='inherit' aria-hidden='true' />
              </Button>
            )}
          </div>

          <Button onClick={openMaintenance} data-testid='alarms-to-triage-cta'>
            <span>{state === 'empty' ? t('dashboard.reviewTriagedAlarms') : t('dashboard.triageAll')}</span>
            <ChevronRightIcon fontSize='inherit' aria-hidden='true' />
          </Button>
        </div>
      </CardHeader>

      {state === 'loading' && (
        <CardContent className='ds-px-8 ds-pb-8 ds-pt-0'><BreakdownSkeleton /></CardContent>
      )}

      {state === 'populated' && (
        <CardContent className='ds-px-8 ds-pb-8 ds-pt-0'>
          <SeverityBreakdown segments={segments} onSegmentClick={openMaintenance} />
        </CardContent>
      )}
    </Card>
  );
}
